import React, { Component } from "react";
import cns from "classnames";
import StickyContainer, { StickyContainerProps } from "@components/StickyContainer";
import { withStyles, Paper } from "@material-ui/core";
import { ThemeType, Classes } from "@theme";
import { ClearSearch, ClearSearchProps } from "@search";

export type SearchPageLayoutProps = {
  delayed?: boolean;
  actionBar?: React.ReactNode;
  beforeContent?: React.ReactNode;
  content: React.ReactNode | ((scrollContainer: HTMLElement) => React.ReactNode);
  ClearSearchProps?: Partial<ClearSearchProps>;
  filter: React.ReactNode;
  header?: React.ReactNode;
  onClear?: () => void;
  newButton?: React.ReactNode;
  showContentOverlay?: boolean;
  stickyContainerProps?: Partial<StickyContainerProps>;
  wrapContent?: (content: React.ReactNode) => React.ReactNode;

  classes: Classes<typeof styles>;
};

type State = {
  scrollContainer: HTMLElement;
};

/**
 * Search page layout
 * topbar holds a filter
 * content displays search results
 */
export class SearchPageLayout extends Component<SearchPageLayoutProps, State> {
  static defaultProps: Partial<SearchPageLayoutProps> = {
    wrapContent: (content) => content
  };

  state: State = {
    scrollContainer: null
  };

  componentDidMount(): void {
    this.setState({
      scrollContainer: document.getElementById("search_page_scrollable")
    });
  }

  render() {
    const { classes, delayed, showContentOverlay, wrapContent } = this.props;
    const { scrollContainer } = this.state;

    const content = delayed
      ? scrollContainer
        ? this.renderContent()
        : null
      : this.renderContent();

    return (
      <div
        className={cns(classes.root, { [classes.overlaid]: showContentOverlay })}
        data-test="SearchPageLayout"
      >
        <div className={classes.content} id="search_page_scrollable">
          {wrapContent(content)}
        </div>
      </div>
    );
  }

  private renderContent = () => {
    const {
      actionBar,
      beforeContent,
      classes,
      content,
      filter,
      header,
      newButton,
      onClear
    } = this.props;
    const { scrollContainer } = this.state;

    return (
      <>
        <StickyContainer
          getScrollContainer={() => scrollContainer}
          {...this.props.stickyContainerProps}
        >
          <Paper style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
            {onClear && (
              <ClearSearch onClick={onClear} {...this.props.ClearSearchProps} />
            )}
            {header}
            <div className={classes.topbar}>{filter}</div>
            {actionBar && <div className={classes.actionBar}>{actionBar}</div>}
          </Paper>
        </StickyContainer>

        {newButton && (
          <div className={classes.buttonWrapper}>
            <div className={classes.addButton}>{newButton}</div>
          </div>
        )}

        {beforeContent}

        {typeof content === "function" ? content(scrollContainer) : content}
      </>
    );
  };
}

const styles = (theme: ThemeType) => ({
  root: {
    flex: 1,
    display: "flex",
    flexDirection: "column"
  },

  actionBar: {
    padding: 0
  },

  topbar: {
    flex: 0,
    paddingBottom: 0,
    position: "relative"
  },

  content: {
    flex: "1 0 0",
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    overflowY: "scroll"
  },

  stickyContainer: {
    background: theme.palette.background,
    boxShadow: theme.shadows[1]
  },

  buttonWrapper: { position: "relative" },

  addButton: {
    position: "absolute",
    zIndex: 1,
    top: -27,
    right: 40
  },

  overlaid: {
    ...theme.mixins.withOverlay()
  }
});

export default withStyles(styles as any, { name: "SearchPageLayout" })(SearchPageLayout);
